/**
 * How a studio engine tells the callbacks a host handed it, stated once.
 *
 * One rule for every engine of this package. Every listener is told. A listener that throws stops
 * neither the engine nor the listeners after it. The first fault of a round reaches the program
 * as an uncaught error on a microtask, and never as the outcome of a command: one report per
 * round, so a defect every listener shares is raised once. An engine imports the two
 * functions below and never writes the loop or the microtask itself.
 */

/**
 * surfaceFault hands a callback's own fault to the runtime as an uncaught error, on a microtask.
 *
 * A callback that throws is a defect of the program that handed it over. Thrown through the
 * engine it would read as the engine's own command failing, and dropped it would hide the defect,
 * so it leaves the engine's flow and reaches the program's own uncaught-error handling.
 *
 * It is the answer for a single callback too: a reporter or a sink an engine calls inside a
 * `try`, whose `catch` hands the fault here.
 *
 * @param fault What the callback threw, as thrown.
 */
export const surfaceFault = (fault: unknown): void => {
  queueMicrotask(() => {
    throw fault;
  });
};

/**
 * notifyListeners tells every listener, whatever one of them does. It never throws.
 *
 * The listeners are told in the order they are iterated, over a copy: the listeners held when the
 * round starts are the round, so one that subscribes or detaches while being told changes it for
 * nobody. The first fault a listener throws is handed to {@link surfaceFault} once they have all
 * been told, so no caller has to decide what a listener's exception does to a command.
 *
 * @param listeners The callbacks to tell.
 * @param args What each one is told, for a fan-out that carries an event.
 */
export const notifyListeners = <TArgs extends readonly unknown[]>(
  listeners: Iterable<(...args: TArgs) => void>,
  ...args: TArgs
): void => {
  let fault: { readonly thrown: unknown } | undefined;

  for (const listener of [...listeners]) {
    try {
      listener(...args);
    } catch (thrown) {
      fault ??= { thrown };
    }
  }

  if (fault !== undefined) {
    surfaceFault(fault.thrown);
  }
};
